import {
  ActionFunctionArgs,
  LoaderFunction,
  LoaderFunctionArgs,
  useActionData,
  useLoaderData,
  useParams,
  ShouldRevalidateFunction,
  useSubmit,
} from 'react-router';
import { addPlanMileage } from '@/catalog/utils/actions/add-plan-mileage';
import { addVehicleAndInsurancePlans } from '@/catalog/utils/actions/add-vehicle-and_insurance_plans';
import { addZipcode } from '@/catalog/utils/actions/add-zipcode';
import { appFetch } from '@/util-react-router/app-fetch';
import { appData } from '@/util-react-router/app-data';
import { appRedirect } from '@/util-react-router/app-redirect';
import { buildPageMeta } from '@/util-react-router/build-page-meta';
import { findNearestZipcode } from '@/catalog/utils/actions/find-nearest-zipcode';
import { fullstoryCheckoutPropsFromSession } from '@/fullstory/utils/fullstory-checkout-props-from-session';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import { getPDPPageTitle } from '@/catalog/utils/get-pdp-page-title';
import {
  ORDER_MARKET_VEHICLE_INVALID_MESSAGE,
  ORDER_VEHICLE_NOT_AVAILABLE_MESSAGE,
} from '@/checkout/constants/checkout-messages';
import { PDPAction } from '@/catalog/types/pdp-action';
import { PDPLoader } from '@/catalog/types/pdp-loader';
import { shouldShowViewCount } from '@/catalog/utils/should-show-view-count';
import { submitAddSavingsCalculatorParams } from '@/catalog/utils/actions/submit-add-savings-calculator-params';
import { submitCreditAge } from '@/catalog/utils/actions/submit-credit-age';
import { submitPayOverdueBalance } from '@/account/utils/actions/submit-pay-overdue-balance';
import { submitUpdateAndPayOverdueBalance } from '@/account/utils/actions/submit-update-and-pay-overdue-balance';
import { submitVehicleCostSavingsCalculation } from '@/catalog/utils/actions/submit-vehicle-cost-savings-calculation';
import { submitVehiclePlan } from '@/catalog/utils/actions/submit-vehicle-plan';
import { useEffect, useRef, useState } from 'react';
import { useFullstoryPageLoadEvent } from '@/fullstory/hooks/use-fullstory-page-load-event';
import { usePushVehicleVariablesToDataLayer } from '@/common/hooks/google-tag-manager/use-push-vehicle-variables-to-data-layer';
import { UserFlow, getRedirectPath } from '@/common/utils/get-redirect-path';
import { useSendGTMEvent } from '@/common/hooks/google-tag-manager/use-send-gtm-event';
import { useStripeConfig } from '@/checkout/hooks/use-stripe-config';
import BreadcrumbBack from '@/common/components/breadcrumb-back';
import hasCompletedIntroSelections from '@/catalog/utils/has-completed-intro-selections';
import logResponseError from '@/util-react-router/log-response-error';
import PageErrorBanners from '@/common/components/page-error-banners';
import PageLayout, { withPageContainerStyles } from '@/common/components/page-layout';
import SavingsCalculatorV2DrawerDialog from '@/catalog/components/savings-calculator-v2/savings-calculator-v2-drawer-dialog';
import Spacer from '@/common/components/spacer';
import StatusBanner from '@/common/components/status-banner';
import ZipCreditAgeMultiStepDrawerDialog from '@/catalog/components/intro-modal/zipcode-credit-age-multi-step-drawer-dialog';
import { updatePlanMileageCreditAndAge } from '@/catalog/utils/actions/update-plan-mileage-credit-and-age';
import { useDigiohModalPageWrapper } from '@/common/hooks/digioh/use-digioh-modal-page-wrapper';
import { PAYMENT_HTTP_ERROR_CODE } from '@/common/constants/status-codes';
import GasSaverBanner from '@/catalog/components/gas-saver-banner';
import getGasSaverCentValue from '@/common/utils/get-gas-saver-cent-value';
import HeaderContainer from '@/catalog/components/header/header-container';
import { getMilesPerYearOptions } from '@/catalog/utils/get-miles-per-year-options';
import { useFullstorySendEventOnce } from '@/fullstory/hooks/use-fullstory-send-event-once';
import { colors } from '@/theme/colors';
import PlanSelectionPanel from '@/catalog/components/pdp/plan-selection-panel/plan-selection-panel';
import VehicleInfo from '@/catalog/components/pdp/vehicle-info';
import ImagePreviewContainer from '@/catalog/components/pdp/image-preview-container';
import { addInsuranceTier } from '@/catalog/utils/actions/add-insurance-tier';
import FeaturesSpecs from '@/catalog/components/pdp/features-specs';
import WhyFlexcar from '@/catalog/components/pdp/why-flexcar';
import Inspection from '@/catalog/components/pdp/inspection';
import FloatyBar from '@/catalog/components/pdp/floaty-bar';
import useFloatyBar from '@/catalog/hooks/use-floaty-bar';
import ScrollToTopButton from '@/common/components/scroll-to-top-button';
import { filterVehiclePlansByMiles } from '@/catalog/utils/filter-vehicle-plan';
import usePlanSelectionErrorGuides from '@/catalog/hooks/use-plan-selection-error-guides';
import CostComparison from '@/catalog/components/pdp/cost-comparison';
import PromoCodeBanner from '@/catalog/components/promo-code-banner';
import getPromoCode from '@/common/utils/get-promo-code';
import VehiclePills from '@/catalog/components/pdp/vehicle-pills/vehicle-pills';
import SeeHowToSave from '@/catalog/components/pdp/see-how-to-save';
import { shouldShowSeeHowToSave } from '@/catalog/utils/should-show-see-how-to-save';
import { registerVehicleView } from '@/catalog/utils/actions/register-vehicle-view';
import useRegisterVehicleView from '@/catalog/hooks/use-register-vehicle-view';
import { redirectMismatchedMarket } from '@/catalog/utils/actions/redirect-mismatched-market';

export const meta = ({ data }: { data: PDPLoader }) => {
  const { title, description } = getPDPPageTitle(data);

  // If we have a thumbnail image, use it for the meta image
  const image =
    data?.PUBLIC_CDN_URL && data?.details?.vehicle?.thumbnail_image?.path
      ? `${data.PUBLIC_CDN_URL}/${data.details.vehicle.thumbnail_image.path}.webp`
      : undefined;

  return buildPageMeta({ title, description, image });
};

export async function action({ params, request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get('intent');

  switch (intent) {
    // Submit plan selection for vehicle
    case 'plan': {
      const { response, errors } = await submitVehiclePlan({ formData, request, params });

      if (errors) {
        return { errors };
      }

      if (!response.ok) {
        logResponseError('pdp - plan - action', response);
        return { errors: { error: 'There was an issue selecting your plan.' } };
      }

      return appRedirect({
        url: '/schedule',
        response,
      });
    }
    // Change credit score and age
    case 'add_credit_and_age': {
      const { response } = await submitCreditAge({ formData, request });

      if (!response.ok) {
        logResponseError('pdp - add_credit_and_age - action', response);
        return {
          errors: {
            error: 'There was an issue updating your selected credit score and age.',
          },
        };
      }

      return appData({
        data: { ok: true, intent, closeIntroModal: true },
        response,
      });
    }
    case 'pay-overdue-balance': {
      const { response } = await submitPayOverdueBalance({
        request,
      });

      if (response.ok) {
        return appData({
          data: { ok: true, intent },
          response,
        });
      } else {
        const { message } = await response.json();

        logResponseError('pdp - pay-overdue-balance - action', response);
        return {
          errors: {
            payment: {
              error:
                response.status !== PAYMENT_HTTP_ERROR_CODE
                  ? 'There was an issue processing your payment. Please try again later.'
                  : message,
            },
          },
        };
      }
    }
    case 'update-and-pay-overdue-balance': {
      const { errors, response } = await submitUpdateAndPayOverdueBalance({
        formData,
        request,
      });

      if (errors) {
        return { errors };
      }

      if (response.ok) {
        return appData({
          data: { ok: true, intent },
          response,
        });
      } else {
        const { message } = await response.json();

        logResponseError('pdp - update-and-pay-overdue-balance - action', response);
        return {
          errors: {
            updatePayment: {
              error:
                response?.status === PAYMENT_HTTP_ERROR_CODE
                  ? message
                  : 'There was an issue processing your payment. Please try again later.',
            },
          },
        };
      }
    }
    // Update selected plan mileage in session cart so that the total savings calculation
    // above plan selection is updated
    case 'update_selected_plan_mileage': {
      return await addPlanMileage({ formData, request });
    }
    case 'update_selected_insurance_tier': {
      return await addInsuranceTier({ formData, request });
    }
    case 'plan-with-insurance': {
      const { response, errors } = await addVehicleAndInsurancePlans({
        formData,
        request,
        params,
      });

      if (errors) {
        return { errors };
      }

      if (!response.ok) {
        logResponseError('pdp - plan - action', response);
        return { errors: { error: 'There was an issue selecting your plan.' } };
      }

      return appRedirect({
        url: '/schedule',
        response,
      });
    }
    case 'calculate_vehicle_cost_savings': {
      try {
        const shouldBreakoutInsurance = process.env.SHOULD_BREAKOUT_INSURANCE === 'true';

        const { errors, response } = await submitVehicleCostSavingsCalculation({
          formData,
          request,
          params,
          shouldBreakoutInsurance,
        });

        if (errors) {
          return { errors };
        }

        if (response.ok) {
          const jsonResponse = await response.json();
          return appData({
            data: { ok: true, savings: jsonResponse.vehicle.savings },
            response,
          });
        } else {
          logResponseError('pdp - calculate_vehicle_cost_savings - action', response);
          return {
            errors: {
              savings: 'Failed to calculate vehicle cost savings.',
            },
          };
        }
      } catch {
        return {
          errors: {
            savings: 'Failed to calculate vehicle cost savings.',
          },
        };
      }
    }
    case 'add_savings_calculator_inputs': {
      try {
        const { errors, response } = await submitAddSavingsCalculatorParams({
          formData,
          request,
        });

        if (errors) {
          return { errors };
        }

        if (!response.ok) {
          logResponseError(
            'pdp - add_savings_calculator_inputs - calculator v2 - action',
            response
          );
          return {
            errors: {
              error: 'Failed to calculate savings.',
            },
          };
        }

        const data = await response.json();

        return appData({
          data: {
            ok: true,
            intent,
            savings_calculator: data.vehicle.savings_calculator,
          },
          response,
        });
      } catch {
        return {
          errors: {
            savings: 'Failed to calculate savings.',
          },
        };
      }
    }
    case 'add_zipcode': {
      return await addZipcode({ formData, request, params });
    }
    case 'find_nearest_zipcode': {
      return await findNearestZipcode({ formData, request });
    }
    case 'update_plan_mileage_credit_and_age': {
      return await updatePlanMileageCreditAndAge({ formData, request });
    }
    case 'register_vehicle_view': {
      return await registerVehicleView({ request, params });
    }
    default: {
      return { errors: { error: 'Invalid intent' } };
    }
  }
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  actionResult,
  defaultShouldRevalidate,
}) => {
  /**
   * Don't revalidate on the fullstory session POST. That post retriggers the
   * loader and will cause the page view count to increment twice instead of just once
   * on page load.
   *
   * Also do not revalidate for saving calculator actions, there is no new data needed from the loader
   * because the new calculator data comes from the action data.
   *
   * Also don't revalidate when registering a vehicle view, we only want to register the view once.
   */
  if (
    actionResult?.ok &&
    (actionResult?.intent === 'fullstory-session' ||
      actionResult?.intent === 'find_nearest_zipcode' ||
      actionResult?.intent === 'add_zipcode' ||
      actionResult?.intent === 'calculate_vehicle_cost_savings' ||
      actionResult?.intent === 'add_savings_calculator_inputs' ||
      actionResult?.intent === 'register_vehicle_view')
  ) {
    return false;
  }

  return defaultShouldRevalidate;
};

export const loader: LoaderFunction = async ({ params, request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const currentPath = url.pathname;

  const [
    vehicleResponse,
    savingsCalculatorResponse,
    marketsResponse,
    lotsResponse,
    creditResponse,
  ] = await Promise.all([
    appFetch({
      url: `/api/external/v1/catalog/${params.vehicleId}?market_id=${params.market}`,
      request,
    }),
    appFetch({
      url: `api/external/v1/catalog/savings_calculator?market_id=${params.market}&vehicle_id=${params.vehicleId}`,
      request,
    }),
    appFetch({
      url: '/api/external/v1/markets/',
      request,
    }),
    appFetch({
      url: `/api/external/v1/appointments/market_lots?market_id=${params.market}`,
      request,
    }),
    appFetch({ url: `/api/external/v1/risk/credit_scores_and_ages`, request }),
  ]);

  if (!vehicleResponse.ok) {
    logResponseError('pdp - vehicle - loader', vehicleResponse);
    return { errors: { error: 'Failed to fetch vehicle' } };
  }

  if (!savingsCalculatorResponse.ok) {
    // Only log here, we do not want to fail the page on this response
    logResponseError('pdp - savings-calculator - loader', savingsCalculatorResponse);
  }

  if (!marketsResponse.ok) {
    logResponseError('pdp - markets - loader', marketsResponse);
    return { errors: { error: 'Failed to fetch markets' } };
  }

  if (!lotsResponse.ok) {
    logResponseError('pdp - lots - loader', lotsResponse);
    return { errors: { error: 'Failed to fetch lots' } };
  }

  if (!creditResponse.ok) {
    logResponseError('pdp - credit - loader', creditResponse);
    return { errors: { error: 'Failed to fetch credit and age' } };
  }

  const [vehicleData, savingsData, marketsData, lotsData, creditAgeData] =
    await Promise.all([
      vehicleResponse.json(),
      savingsCalculatorResponse.json(),
      marketsResponse.json(),
      lotsResponse.json(),
      creditResponse.json(),
    ]);

  const {
    session,
    billing_address,
    payment_methods,
    stripe_enabled,
    stripe_customer_session_client_secret,
    subscription,
    unpaid_balance,
    vehicle,
    active_insurance_plan_tier,
    is_market_mismatch,
    has_active_membership,
    show_membership_test_a,
    show_membership_test_b,
  } = vehicleData;

  const redirectPath = getRedirectPath({
    path: currentPath,
    flow: session?.can_swap ? UserFlow.swap : UserFlow.checkout,
    session,
  });

  if (redirectPath) {
    return appRedirect({
      url: redirectPath,
      response: vehicleResponse,
    });
  }

  const searchParams = url.searchParams.toString();
  const mismatchRedirect = redirectMismatchedMarket({
    sessionMarketId: session?.cart?.market_id,
    paramMarketId: params.market,
    paramVehicleId: params.vehicleId,
    queryParams: searchParams,
    response: vehicleResponse,
  });

  if (mismatchRedirect) {
    return mismatchRedirect;
  }

  const milageOptions = process.env.MILEAGE_PLAN_OPTIONS?.split(',');

  const filteredVehiclePlans = filterVehiclePlansByMiles({
    vehiclePlans: vehicle?.plans,
    milageOptions,
    shouldFilter: subscription?.can_swap !== true,
  });

  return appData({
    data: {
      session,
      details: {
        billing_address,
        payment_methods,
        stripe_enabled,
        stripe_customer_session_client_secret,
        subscription,
        unpaid_balance,
        has_active_membership,
        vehicle: {
          ...vehicle,
          mileage_plan_ordering: vehicle?.plans.map((plan: { id: number }) => plan.id),
          savings_calculator: savingsData?.vehicle?.savings_calculator,
          plans: filteredVehiclePlans,
          // The current view count is added after the client renders the page so to show the correct view count
          // we need to add 1 to the view count here
          view_count:
            vehicle?.view_count !== undefined ? vehicle?.view_count + 1 : undefined,
        },
        active_insurance_plan_tier,
        is_market_mismatch,
        show_membership_test_a,
        show_membership_test_b,
      },
      creditScores: creditAgeData.credit_scores,
      ageRanges: creditAgeData.age_ranges,
      PUBLIC_CDN_URL: process.env.PUBLIC_CDN_URL,
      ADYEN_ORIGIN_KEY: process.env.ADYEN_ORIGIN_KEY,
      ADYEN_JS_SRC: process.env.ADYEN_JS_SRC,
      SMARTYSTREETS_EMBEDDED_KEY: process.env.SMARTYSTREETS_EMBEDDED_KEY,
      MOST_POPULAR_PLAN_MILEAGE_AMOUNT: process.env.MOST_POPULAR_PLAN_MILEAGE_AMOUNT,
      SHOULD_SHOW_PDP_VIEW_COUNT: process.env.SHOULD_SHOW_PDP_VIEW_COUNT === 'true',
      VIEW_COUNT_THRESHOLD: process.env.VIEW_COUNT_THRESHOLD,
      ENABLE_INSURANCE_CHANGE: process.env.ENABLE_INSURANCE_CHANGE === 'true',
      MEMBERSHIP_COST: process.env.MEMBERSHIP_COST,
      STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
      GAS_SAVER_BANNER: process.env.GAS_SAVER_BANNER === 'true',
      MOST_POPULAR_PLAN_MILEAGE_AMOUNT_TWELVE_HUNDRED:
        process.env.MOST_POPULAR_PLAN_MILEAGE_AMOUNT_TWELVE_HUNDRED,
      MILEAGE_PLAN_OPTIONS: milageOptions,
      // Below are deprecated values that we still need to pass down for clients with cached code
      markets: marketsData.markets,
      lots: lotsData.lots,
      SHOULD_BREAKOUT_INSURANCE: true,
      MOST_POPULAR_PLAN_MILEAGE_PERCENTAGE:
        process.env.MOST_POPULAR_PLAN_MILEAGE_PERCENTAGE,
      PRISTINE_HERO_PDP: true,
    },
    response: vehicleResponse,
  });
};

export default function VehicleDetails() {
  const actionData = useActionData<PDPAction>();

  const {
    details,
    creditScores,
    ageRanges,
    PUBLIC_CDN_URL,
    STRIPE_PUBLISHABLE_KEY,
    ADYEN_ORIGIN_KEY,
    ADYEN_JS_SRC,
    SMARTYSTREETS_EMBEDDED_KEY,
    MOST_POPULAR_PLAN_MILEAGE_AMOUNT,
    SHOULD_SHOW_PDP_VIEW_COUNT,
    VIEW_COUNT_THRESHOLD,
    ENABLE_INSURANCE_CHANGE,
    MEMBERSHIP_COST,
    GAS_SAVER_BANNER,
    session,
    errors,
    MOST_POPULAR_PLAN_MILEAGE_AMOUNT_TWELVE_HUNDRED,
    MILEAGE_PLAN_OPTIONS,
  } = useLoaderData<PDPLoader>();
  useRegisterVehicleView();
  const params = useParams();
  const introSelections = hasCompletedIntroSelections({
    selectedCreditScoreId: session?.cart?.credit_score_id,
    selectedZipcode: session?.cart?.zipcode,
    selectedIsOver25: session?.cart?.is_over_25,
    selectedAgeRangeId: session?.cart?.age_range_id,
    isUsingAgeRange: ageRanges !== undefined && ageRanges?.length > 0,
    ageRanges: ageRanges ?? [],
  });
  const { promoCode, promoCodeReward } = getPromoCode({ session });
  const showPromoCodeBanner = promoCode !== undefined && promoCodeReward !== undefined;

  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [savingsCalculatorOpen, setSavingsCalculatorOpen] = useState(false);
  const [hasChangedSelectedPlan, setHasChangedSelectedPlan] = useState(false);
  const [introModalOpen, setIntroModalOpen] = useState(
    !introSelections.hasSelectedZipcodeCreditAge ? true : false
  );
  const introModalHandleClose = () => setIntroModalOpen(false);
  const introModalHandleOpen = () => setIntroModalOpen(true);

  const handleSavingsCalculatorClose = () => setSavingsCalculatorOpen(false);
  const submit = useSubmit();

  const stripeConfig = useStripeConfig({
    publishableKey: STRIPE_PUBLISHABLE_KEY,
    customerSessionClientSecret: details?.stripe_customer_session_client_secret,
  });

  const {
    dismissedPlanGuide,
    mileagePlanSectionRef,
    insurancePlanSectionRef,
    scrollToMileagePlanSection,
    scrollToInsurancePlanSection,
  } = usePlanSelectionErrorGuides();

  useEffect(() => {
    if (actionData?.ok) {
      switch (actionData.intent) {
        case 'add_credit_and_age':
          if (actionData.closeIntroModal) {
            setCreditModalOpen(false);
          }
          break;
        case 'add_plan_mileage':
          setHasChangedSelectedPlan(true);
          break;
      }
    }
    if (actionData?.errors) {
      // We only return one of these errors at a time so that we can scroll to the appropriate section and display the plan guide popover for the correct section
      if (actionData.errors.plan) {
        scrollToMileagePlanSection();
      }
      if (actionData.errors.insurancePlan) {
        scrollToInsurancePlanSection();
      }
    }
  }, [actionData, scrollToInsurancePlanSection, scrollToMileagePlanSection]);

  useEffect(() => {
    if (introSelections.hasSelectedZipcodeCreditAge && introModalOpen) {
      setIntroModalOpen(false);
    }
  }, [introModalOpen, introSelections, introSelections.hasSelectedZipcodeCreditAge]);

  const shouldDisplayViewCount = shouldShowViewCount(
    SHOULD_SHOW_PDP_VIEW_COUNT,
    details?.vehicle?.view_count ?? 0,
    VIEW_COUNT_THRESHOLD
  );

  const vehicleCartImage = details?.vehicle?.detail_images?.[0]?.path
    ? `${PUBLIC_CDN_URL}/${details.vehicle.detail_images[0].path}`
    : undefined;
  const isOrderableInMarket: boolean =
    (details?.vehicle?.is_orderable ?? false) && details?.is_market_mismatch !== true;
  const hasError = errors?.error !== undefined || actionData?.errors !== undefined;

  const hasSentVehicleVariablesToDataLayer = usePushVehicleVariablesToDataLayer(
    details?.vehicle,
    vehicleCartImage,
    session?.cart?.age_range_id,
    session?.cart?.credit_score_id,
    session?.fullstory_params?.checkout?.market,
    session?.fullstory_params?.checkout?.body_type
      ? `${session?.fullstory_params?.checkout?.body_type}`
      : undefined,
    session?.fullstory_params?.checkout?.standardized_base_price,
    session?.fullstory_params?.checkout?.cross_market
  );

  // This event should always be sent after the vehicle variables are pushed to the data layer.
  useSendGTMEvent({
    event_name: 'visit_vehicle_detail',
    condition: hasSentVehicleVariablesToDataLayer,
  });

  useFullstoryPageLoadEvent('visit_vehicle_detail', {}, session);

  const fullstoryPlanEventDelegate = () => {
    fullstoryEvent('plan_selected', fullstoryCheckoutPropsFromSession(session));
  };

  const fullstoryContinueEventDelegate = () => {
    fullstoryEvent('continue_to_schedule', fullstoryCheckoutPropsFromSession(session));
  };

  const fullstoryInsuranceEventDelegate = () => {
    fullstoryEvent('insurance_plan_selected', {
      inventory_id: session?.fullstory_params?.checkout?.inventory_id || '',
      market: session?.fullstory_params?.checkout?.market || '',
    });
  };

  const fullstoryOpenSeeHowToSaveEventDelegate = () => {
    fullstoryEvent('open_see_how_to_save');
  };

  const fullstoryOpenPlanPanelSavingsEventDelegate = () => {
    fullstoryEvent('open_plan_panel_savings');
  };

  const fullstoryOpenMobileStickyFooterSavingsEventDelegate = () => {
    fullstoryEvent('open_mobile_sticky_footer_savings');
  };

  const fullstoryOpenFloatyBarSavingsEventDelegate = () => {
    fullstoryEvent('open_floaty_bar_savings');
  };

  const fullstoryOpenSavingsPanelSavingsEventDelegate = () => {
    fullstoryEvent('open_savings_panel_savings');
  };

  const handleSavingsClickWithFullstoryEvent = (fullstoryEventDelegate?: () => void) => {
    fullstoryEventDelegate?.();
    setSavingsCalculatorOpen(true);
  };

  useDigiohModalPageWrapper({ pageSource: 'vehicle_detail' });

  useFullstorySendEventOnce(
    details?.has_active_membership === false,
    'membership_copy_test_pdp',
    {
      control: !details?.show_membership_test_a && !details?.show_membership_test_b,
      test_a: details?.show_membership_test_a === true,
      test_b: details?.show_membership_test_b === true,
    }
  );

  const headerContent = (
    <HeaderContainer
      session={session}
      dark={false}
      pillClassName="bg-flexcar-neutral100 md:bg-flexcar-neutral100 lg:bg-flexcar-neutral100"
    />
  );

  const gasSaverCents = getGasSaverCentValue(session?.cart?.market_id);

  const {
    continueButtonRef,
    showFloatyBar,
    currentSection,
    onInViewChange,
    sectionRefs,
    addSectionRef,
  } = useFloatyBar();

  const topRef = useRef<HTMLDivElement>(null);
  const vehicleImagesColumnRef = useRef<HTMLDivElement>(null);

  const showSeeHowToSave = shouldShowSeeHowToSave(
    details?.vehicle?.vehicle_savings_calculator_available,
    actionData?.savings_calculator
      ? actionData.savings_calculator.flexcar_savings_over_ownership_year_term
      : details?.vehicle?.savings_calculator?.flexcar_savings_over_ownership_year_term
  );

  return (
    <>
      {GAS_SAVER_BANNER ? (
        <GasSaverBanner
          ref={topRef}
          gasSaverCents={gasSaverCents}
          variant="gray-bg-no-spacer"
        />
      ) : null}

      {showPromoCodeBanner ? (
        <PromoCodeBanner
          ref={GAS_SAVER_BANNER ? undefined : topRef}
          promoCode={promoCode}
          reward={promoCodeReward}
          variant="light-gray-bg-no-spacer"
          withBottomBorder
        />
      ) : null}

      <PageLayout
        disablePageContainer={true}
        disableFooterMargin={true}
        hideCatalogLink={true}
        headerLeftContent={headerContent}
        backgroundColor="bg-flexcar-neutral90"
        headerContentSpacerVariant="no-spacer"
        navLinkClassName="text-flexcar-neutral1"
        hamburgerFillPrimary={colors.neutral1}
        compactMobile
        to={`/inventory/${params.market}`}
        ref={showPromoCodeBanner || GAS_SAVER_BANNER ? undefined : topRef}
        footerClassName="mb-[80px] lg:mb-0"
      >
        {details?.vehicle?.is_orderable === false ? (
          <>
            <div
              className={withPageContainerStyles(
                'max-w-[1440px] flex-col px-[24px] lg:flex-row'
              )}
            >
              <StatusBanner
                variant="info"
                message={{
                  text: ORDER_VEHICLE_NOT_AVAILABLE_MESSAGE,
                }}
              />
            </div>
            <Spacer variant="height24" />
          </>
        ) : null}

        {details?.is_market_mismatch === true ? (
          <>
            <div
              className={withPageContainerStyles(
                'max-w-[1440px] flex-col px-[24px] lg:flex-row'
              )}
            >
              <StatusBanner
                variant="info"
                message={{
                  text: ORDER_MARKET_VEHICLE_INVALID_MESSAGE,
                }}
              />
            </div>
            <Spacer variant="height24" />
          </>
        ) : null}

        {hasError ? (
          <>
            <div
              className={withPageContainerStyles(
                'max-w-[1440px] flex-col px-[24px] lg:flex-row'
              )}
            >
              <PageErrorBanners loaderErrors={errors} actionErrors={actionData?.errors} />
            </div>
            <Spacer variant="height24" />
          </>
        ) : null}

        {details?.vehicle ? (
          <>
            <div
              className={withPageContainerStyles(
                'max-w-[1440px] flex-col gap-0 px-0 md:px-[24px] lg:flex-row lg:gap-[40px]'
              )}
            >
              {/* Left / Top */}
              <div
                className="top-0 flex h-fit w-full flex-col lg:sticky"
                ref={vehicleImagesColumnRef}
              >
                <BreadcrumbBack
                  to={`/inventory/${params.market}`}
                  text="Back"
                  hideSpacerForBreakpoints={['sm', 'md', 'lg', 'xl', '2xl']}
                  toInventory
                  className="body-tight mt-[16px] hidden items-center text-[18px] text-flexcar-neutral0 md:flex"
                />

                <Spacer variant="height32" hideForBreakpoints={['sm']} />
                <Spacer
                  variant="height16"
                  hideForBreakpoints={['md', 'lg', 'xl', '2xl']}
                />

                <VehiclePills
                  isNewVehicle={details.vehicle.is_new_vehicle}
                  showViewCount={shouldDisplayViewCount}
                  viewCount={details?.vehicle?.view_count ?? 0}
                />

                <ImagePreviewContainer
                  images={details?.vehicle?.vehicle_images_meta}
                  cdnUrl={PUBLIC_CDN_URL}
                  mobileInterstitialContent={<VehicleInfo vehicle={details.vehicle} />}
                />
              </div>

              {/* Right / Bottom */}
              <div className="flex w-full flex-shrink-0 flex-col lg:w-[420px]">
                {/* Vehicle info + vehicle pills */}
                <div className="hidden lg:block">
                  <Spacer variant="height40" />
                  <VehicleInfo vehicle={details.vehicle} />
                </div>

                {/* Savings modal's "See how" */}
                {details?.vehicle?.savings_calculator && showSeeHowToSave ? (
                  <>
                    <Spacer variant="height24" />
                    <SeeHowToSave
                      savings={
                        actionData?.savings_calculator
                          ? actionData.savings_calculator
                              .flexcar_savings_over_ownership_year_term
                          : details?.vehicle?.savings_calculator
                              ?.flexcar_savings_over_ownership_year_term
                      }
                      onClickDelegate={() =>
                        handleSavingsClickWithFullstoryEvent(
                          fullstoryOpenSeeHowToSaveEventDelegate
                        )
                      }
                    />
                    <Spacer variant="height24" />
                  </>
                ) : (
                  <Spacer variant="height40" />
                )}

                {/* Plan selection */}
                <PlanSelectionPanel
                  // Mileage plans
                  vehiclePlans={details.vehicle.plans}
                  selectedMileagePlan={session?.cart?.plan_mileage}
                  currentMileagePlan={
                    details?.subscription?.current_billing_period_summary?.plan_miles
                  }
                  mostPopularPlanMileageAmount={
                    session.cart?.market_id == 'atlanta'
                      ? MOST_POPULAR_PLAN_MILEAGE_AMOUNT_TWELVE_HUNDRED
                      : MOST_POPULAR_PLAN_MILEAGE_AMOUNT
                  }
                  fullstoryPlanEventDelegate={fullstoryPlanEventDelegate}
                  // Insurance plans
                  insurancePlans={details.vehicle.insurance_plans}
                  selectedInsuranceTier={session?.cart?.insurance_tier}
                  currentInsurancePlanTier={details?.active_insurance_plan_tier}
                  enableInsuranceChange={ENABLE_INSURANCE_CHANGE}
                  fullstoryInsuranceEventDelegate={fullstoryInsuranceEventDelegate}
                  // Swaps
                  subscription={details.subscription}
                  // Delinquency
                  delinquencyConfig={{
                    unpaidBalance: details.unpaid_balance,
                    paymentMethodConfiguration: details?.payment_methods,
                    ADYEN_ORIGIN_KEY: ADYEN_ORIGIN_KEY,
                    ADYEN_JS_SRC: ADYEN_JS_SRC,
                    SMARTYSTREETS_EMBEDDED_KEY: SMARTYSTREETS_EMBEDDED_KEY,
                    paymentErrors: actionData?.errors?.payment,
                    updatePaymentErrors: actionData?.errors?.updatePayment,
                    shouldUseStripe: details.stripe_enabled,
                    stripeConfig: stripeConfig,
                  }}
                  // Membership
                  gasSaverCents={gasSaverCents}
                  MEMBERSHIP_COST={MEMBERSHIP_COST}
                  vehicleImagesColumnRef={vehicleImagesColumnRef}
                  // Savings
                  savingsAvailable={
                    details?.vehicle?.vehicle_savings_calculator_available
                  }
                  estimatedSavings={
                    actionData?.savings_calculator
                      ? actionData.savings_calculator
                          .flexcar_savings_over_ownership_year_term
                      : details?.vehicle?.savings_calculator
                          ?.flexcar_savings_over_ownership_year_term
                  }
                  handleSavingsClick={() => setSavingsCalculatorOpen(true)}
                  fullstoryOpenPlanPanelSavingsEventDelegate={
                    fullstoryOpenPlanPanelSavingsEventDelegate
                  }
                  fullstoryOpenMobileStickyFooterSavingsEventDelegate={
                    fullstoryOpenMobileStickyFooterSavingsEventDelegate
                  }
                  // Additional config
                  errorsConfig={{
                    errors: actionData?.errors,
                    dismissedPlanGuide,
                  }}
                  isOrderable={isOrderableInMarket}
                  price_savings_1200_over_850={
                    details.vehicle.price_savings_1200_over_850
                  }
                  price_per_mile_850_plan_for_1200_miles={
                    details.vehicle.price_per_mile_850_plan_for_1200_miles
                  }
                  price_difference_850_vs_1200={
                    details.vehicle.price_difference_850_vs_1200
                  }
                  creditConfig={{
                    creditScores,
                    ageRanges,
                    selectedCreditScoreId: session?.cart?.credit_score_id,
                    selectedIsOver25: session?.cart?.is_over_25,
                    selectedAgeRangeId: session?.cart?.age_range_id,
                    open: creditModalOpen,
                    handleOpen: () => setCreditModalOpen(true),
                    handleClose: () => setCreditModalOpen(false),
                  }}
                  fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
                  continueButtonRef={continueButtonRef}
                  planSelectionRefs={{
                    mileagePlanSectionRef,
                    insurancePlanSectionRef,
                  }}
                  PUBLIC_CDN_URL={PUBLIC_CDN_URL}
                />
              </div>
            </div>

            {/* Floaty bar */}
            <FloatyBar
              selectedPlanMileage={session?.cart?.plan_mileage}
              selectedInsuranceTier={session?.cart?.insurance_tier}
              vehiclePlans={details.vehicle.plans}
              insurancePlans={details.vehicle.insurance_plans}
              savingsAvailable={details?.vehicle?.vehicle_savings_calculator_available}
              estimatedSavings={
                actionData?.savings_calculator
                  ? actionData.savings_calculator.flexcar_savings_over_ownership_year_term
                  : details?.vehicle?.savings_calculator
                      ?.flexcar_savings_over_ownership_year_term
              }
              showFloatyBar={showFloatyBar}
              hasFeatures={details?.vehicle?.features !== undefined}
              hasSavings={details?.vehicle?.savings_calculator !== undefined}
              currentSection={currentSection}
              subscription={details.subscription}
              currentMileagePlan={
                details?.subscription?.current_billing_period_summary?.plan_miles
              }
              currentInsurancePlanTier={details?.active_insurance_plan_tier}
              sectionRefs={sectionRefs}
              handleSavingsClick={() =>
                handleSavingsClickWithFullstoryEvent(
                  fullstoryOpenFloatyBarSavingsEventDelegate
                )
              }
              fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
              GAS_SAVER_BANNER={GAS_SAVER_BANNER}
            />

            {/* To top button */}
            <ScrollToTopButton show={showFloatyBar} refToScrollTo={topRef} />

            {/* Features section */}
            <FeaturesSpecs
              featuresSpecs={details.vehicle.features_spec}
              addSectionRef={addSectionRef}
              onInViewChange={onInViewChange}
              vehicle={details.vehicle}
            />

            {/* Savings calculator section */}
            {details?.vehicle?.savings_calculator ? (
              <CostComparison
                savings={
                  actionData?.savings_calculator
                    ? actionData.savings_calculator
                    : details.vehicle.savings_calculator
                }
                addSectionRef={addSectionRef}
                onInViewChange={onInViewChange}
                onBreakdownClickDelegate={() =>
                  handleSavingsClickWithFullstoryEvent(
                    fullstoryOpenSavingsPanelSavingsEventDelegate
                  )
                }
              />
            ) : null}

            <WhyFlexcar
              addSectionRef={addSectionRef}
              onInViewChange={onInViewChange}
              cdnUrl={PUBLIC_CDN_URL}
            />

            <Inspection
              addSectionRef={addSectionRef}
              onInViewChange={onInViewChange}
              cdnUrl={PUBLIC_CDN_URL}
            />
          </>
        ) : null}

        {/* Ensure we don't show the intro modal if we have a page level error */}
        {introModalOpen && !hasError ? (
          <ZipCreditAgeMultiStepDrawerDialog
            open={introModalOpen}
            handleOpen={introModalHandleOpen}
            handleClose={introModalHandleClose}
            creditScores={creditScores}
            selectedZipcode={session?.cart?.zipcode}
            ageRanges={ageRanges}
            selectedAgeRangeId={session?.cart?.age_range_id}
          />
        ) : null}

        {/* Savings Calculator */}
        {details?.vehicle?.savings_calculator ? (
          <SavingsCalculatorV2DrawerDialog
            // This key will force the dialog to remount on key change so that credit and age updates from FSC are reflected here too
            key={`savings-calculator-pdp-${session?.cart?.plan_mileage}-${session?.cart?.credit_score_id}-${session?.cart?.is_over_25}-${session?.cart?.age_range_id}`}
            open={savingsCalculatorOpen}
            handleClose={handleSavingsCalculatorClose}
            creditScores={creditScores}
            ageRanges={ageRanges}
            selectedPlanMileage={session?.cart?.plan_mileage}
            selectedCreditScoreId={session?.cart?.credit_score_id}
            selectedAge={session?.cart?.is_over_25}
            selectedAgeRangeId={session?.cart?.age_range_id}
            error={actionData?.errors?.error || errors?.error}
            imageUrl={
              details.vehicle.thumbnail_image?.path
                ? `${PUBLIC_CDN_URL}/${details.vehicle.thumbnail_image.path}.webp`
                : undefined
            }
            vehicleId={details.vehicle.id}
            modelYear={details.vehicle.model_year}
            make={details.vehicle.make}
            model={details.vehicle.model}
            trim={details.vehicle.trim}
            odometer={details.vehicle.odometer}
            savings={
              actionData?.savings_calculator
                ? actionData.savings_calculator
                : details.vehicle.savings_calculator
            }
            marketId={params.market}
            submit={submit}
            membershipCost={MEMBERSHIP_COST}
            hasChangedSelectedPlan={hasChangedSelectedPlan}
            hasChangedSelectedPlanDelegate={() => setHasChangedSelectedPlan(false)}
            crossMarket={
              details.vehicle.cross_market_route
                ? {
                    originMarketName:
                      details.vehicle.cross_market_route.origin_market_name,
                    destinationMarketName:
                      details.vehicle.cross_market_route.destination_market_name,
                  }
                : undefined
            }
            openedFrom="pdp"
            cdnUrl={PUBLIC_CDN_URL}
            milesPerYearOptions={
              details?.subscription?.can_swap &&
              details?.subscription?.current_billing_period_summary?.plan_miles &&
              MILEAGE_PLAN_OPTIONS
                ? getMilesPerYearOptions() // Show all values
                : getMilesPerYearOptions(MILEAGE_PLAN_OPTIONS ?? undefined) // Show only env mileage plan options
            }
          />
        ) : null}
      </PageLayout>
    </>
  );
}
